import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, MessageCircle, ArrowLeft, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import Header from "@/components/sections/Header";
import Services from "@/components/sections/Services";
import WhyChooseUs from "@/components/sections/WhyChooseUs";
import Reviews from "@/components/sections/Reviews";
import Faq from "@/components/sections/Faq";
import Footer from "@/components/sections/Footer";
import StickyCallBar from "@/components/StickyCallBar";
import WhatsAppFab from "@/components/WhatsAppFab";
import { getZone, ZONES } from "@/lib/zones";
import { PHONE_DISPLAY, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
import {
  localBusinessSchema, breadcrumbSchema, faqPageSchema, serviceSchema,
} from "@/lib/schemas";
import { SITE_URL } from "@/lib/site";

const HERO_BG =
  "https://images.pexels.com/photos/17720190/pexels-photo-17720190.png?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1700&fm=webp";

export default function ZonePage() {
  const { slug } = useParams();
  const zone = getZone(slug);
  if (!zone) return <Navigate to="/" replace />;

  const canonical = `${SITE_URL}/${zone.slug}`;
  const otherZones = ZONES.filter((z) => z.slug !== zone.slug);

  const jsonLd = [
    localBusinessSchema({ extraAreas: [zone.name] }),
    breadcrumbSchema([
      { name: "Home", url: `${SITE_URL}/` },
      { name: zone.name, url: canonical },
    ]),
    serviceSchema({
      name: `Carro attrezzi ${zone.name}`,
      description: zone.intro,
      url: canonical,
      areaServed: zone.name,
    }),
    faqPageSchema(),
  ];

  return (
    <div className="min-h-screen bg-white text-navy" data-testid={`page-zone-${zone.slug}`}>
      <SEO
        title={zone.title}
        description={zone.description}
        canonical={canonical}
        keywords={zone.keywords}
        image={`${SITE_URL}/og-image.png`}
        jsonLd={jsonLd}
      />

      <Header />
      <main>
        {/* Zone Hero */}
        <section
          data-testid="zone-hero"
          className="relative isolate min-h-[80vh] flex items-end md:items-center overflow-hidden bg-navy"
        >
          <img
            src={HERO_BG}
            alt={`Carro attrezzi ${zone.name} H24 - soccorso stradale rapido`}
            width="1700"
            height="1100"
            className="absolute inset-0 -z-10 h-full w-full object-cover object-center opacity-70"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-navy/80 via-navy/55 to-navy/95" />
          <div className="absolute inset-0 -z-10 hero-grid opacity-30" />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-28 md:pt-32 pb-14 md:pb-20 w-full">
            <Link
              to="/"
              data-testid="zone-back-link"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold"
            >
              <ArrowLeft className="h-4 w-4" /> Tutte le zone
            </Link>

            <div className="mt-4 max-w-3xl animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/20 px-3 py-1.5 text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-white/90">
                <MapPin className="h-3.5 w-3.5 text-brand-orange" />
                {zone.name}
              </span>

              <h1
                data-testid="zone-h1"
                className="mt-5 font-display font-black text-white text-shadow-strong text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              >
                {zone.h1} — Soccorso Stradale Rapido
              </h1>

              <p
                data-testid="zone-intro"
                className="mt-5 text-white/90 text-base sm:text-lg max-w-2xl leading-relaxed"
              >
                {zone.intro}
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href={PHONE_TEL_HREF}
                  data-testid="zone-call-button"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-brand-orange text-white font-bold px-6 py-4 text-lg sm:text-xl animate-pulse-ring btn-glow-orange hover:brightness-110 hover:-translate-y-0.5 transition"
                >
                  <Phone className="h-6 w-6" />
                  Chiama Ora
                  <span className="font-mono">{PHONE_DISPLAY}</span>
                </a>
                <a
                  href={WHATSAPP_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="zone-whatsapp-button"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-whatsapp text-white font-bold px-6 py-4 text-lg sm:text-xl btn-glow-green hover:brightness-110 hover:-translate-y-0.5 transition"
                >
                  <MessageCircle className="h-6 w-6" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        <Services />
        <WhyChooseUs />
        <Reviews />
        <Faq />

        {/* Internal linking to other zones — strong for SEO */}
        <section
          data-testid="zone-internal-links"
          className="py-14 md:py-20 bg-[color:var(--brand-surface)]"
          aria-label="Altre zone servite"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <p className="text-brand-orange text-sm font-bold tracking-[0.2em] uppercase">Altre zone</p>
            <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl text-navy tracking-tight">
              Carro attrezzi <span className="text-brand-orange">in altre zone di Roma</span>
            </h2>
            <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
              {otherZones.map((z) => (
                <li key={z.slug}>
                  <Link
                    to={`/${z.slug}`}
                    data-testid={`zone-link-${z.slug}`}
                    className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2.5 hover:border-brand-orange/50 hover:bg-brand-orange/5 transition text-sm font-medium text-navy"
                  >
                    <MapPin className="h-4 w-4 text-brand-orange" />
                    Carro attrezzi {z.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/"
                  data-testid="zone-link-home"
                  className="flex items-center gap-2 rounded-xl border border-navy bg-navy text-white px-3 py-2.5 hover:bg-brand-orange hover:border-brand-orange transition text-sm font-medium"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Torna alla home
                </Link>
              </li>
            </ul>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
      <StickyCallBar />
      <div aria-hidden className="h-20 md:h-0" />
    </div>
  );
}
